import {
  SYNTH_WAVEFORMS,
  normalizeSynthResource,
  normalizeSynthTrack,
} from '../../stores/synthModel.js'
import { getSynthPlaybackTrackEntries } from '../../stores/synthPlayback.js'
import { eventDurationMs } from '../../stores/synthTiming.js'

const MASTER_ATTENUATION = 0.22
const DEFAULT_SAMPLE_RATE = 44100
const PULSE_HARMONICS = 32
const SCHEDULE_AHEAD_SECONDS = 0.25
const SCHEDULER_INTERVAL_MS = 50
const ANTI_CLICK_RELEASE_SECONDS = 0.005
const MAX_MASTER_PEAK = 0.9
const MAX_TIMESTAMP_INTERPOLATION_SECONDS = 0.1

export class BrowserSynthEngine {
  constructor() {
    this.context = null
    this.masterGain = null
    this.noiseBuffer = null
    this.activeStops = new Set()
    this.playing = false
    this.auditioning = false
    this.auditionStops = []
    this._stopTimers = new Set()
    this.playbackStartTime = null
    this.audioStartContextTime = null
    this.playbackStartOffsetSeconds = 0
    this.playbackDurationSeconds = 0
    this.playbackPositionSeconds = 0
    this.playbackState = 'stopped'
    this.playbackGeneration = 0
    this.schedulerTimer = null
    this.scheduledTracks = []
    this.playbackPlan = null
    this.trackGains = new Map()
    this.trackActiveStops = new Map()
  }

  async play(resource, scope = { type: 'song' }, positionMs = 0) {
    const requestedPositionSeconds = Math.max(0, Number(positionMs) / 1000 || 0)
    this.stop({ resetPosition: false })
    const context = this._ensureContext()
    this.context = context
    await context.resume()
    this.playing = true
    this.playbackState = 'playing'
    const plan = createPlaybackPlan(resource, scope)
    this.playbackPlan = plan
    this._openMasterGain(calculatePlaybackMasterGain(plan))
    this._createTrackGains(plan)
    this.playbackDurationSeconds = plan.totalDurationSeconds
    this.playbackPositionSeconds = this._clampPosition(requestedPositionSeconds)
    const startTime = context.currentTime + 0.05
    this._startScheduling(startTime)
    const stopAt = this._schedulePlaybackEnd()
    return { startTime, stopAt }
  }

  stop({ resetPosition = true } = {}) {
    this._cancelScheduledPlayback()
    this.scheduledTracks = []
    this.playbackPlan = null
    this._disconnectTrackGains()
    this.playing = false
    this.playbackState = 'stopped'
    this.playbackStartTime = null
    this.audioStartContextTime = null
    this.playbackStartOffsetSeconds = 0
    this.playbackDurationSeconds = 0
    if (resetPosition) this.playbackPositionSeconds = 0
    this.stopAudition()
    if (this.masterGain && this.context) {
      rampGainToSilence(this.masterGain.gain, this.context.currentTime)
    }
  }

  isPlaying() {
    return this.playing
  }

  isPaused() {
    return this.playbackState === 'paused'
  }

  getPlaybackTimeMs() {
    if (!this.playing || !this.context || this.audioStartContextTime == null) return this.playbackPositionSeconds * 1000
    const elapsedSeconds = Math.max(0, this._audibleContextTime() - this.audioStartContextTime)
    return this._clampPosition(this.playbackStartOffsetSeconds + elapsedSeconds) * 1000
  }

  pause() {
    if (!this.playing) return this.getPlaybackTimeMs()
    this.playbackPositionSeconds = this.getPlaybackTimeMs() / 1000
    this._cancelScheduledPlayback()
    this.playing = false
    this.playbackState = 'paused'
    this.playbackStartTime = null
    this.audioStartContextTime = null
    this.playbackStartOffsetSeconds = this.playbackPositionSeconds
    return this.playbackPositionSeconds * 1000
  }

  seek(positionMs) {
    this.playbackPositionSeconds = this._clampPosition(Number(positionMs) / 1000)
    if (!this.playbackPlan || !this.context) return this.playbackPositionSeconds * 1000
    if (!this.playing) return this.playbackPositionSeconds * 1000

    this._cancelScheduledPlayback()
    this._openMasterGain(MASTER_ATTENUATION)
    this._startScheduling(this.context.currentTime)
    this._schedulePlaybackEnd()
    return this.playbackPositionSeconds * 1000
  }

  updateTrack(trackIndex, track) {
    const normalized = normalizeSynthTrack(track, trackIndex)
    const plannedTrack = this.playbackPlan?.tracks.find(item => item.trackIndex === trackIndex)
    if (plannedTrack) {
      plannedTrack.track = normalized
      plannedTrack.layers = normalized.layers.slice()
    }
    const scheduledTrack = this.scheduledTracks.find(item => item.trackIndex === trackIndex)
    if (scheduledTrack) {
      scheduledTrack.track = normalized
      scheduledTrack.layers = normalized.layers.slice()
    }
    this.setTrackVolume(trackIndex, normalized.volume)
  }

  setTrackEnabled(trackIndex, enabled) {
    const plannedTrack = this.playbackPlan?.tracks.find(item => item.trackIndex === trackIndex)
    if (plannedTrack) plannedTrack.track.enabled = enabled
    const scheduledTrack = this.scheduledTracks.find(item => item.trackIndex === trackIndex)
    if (scheduledTrack) scheduledTrack.track.enabled = enabled
    if (!enabled) this.stopTrackVoices(trackIndex)
  }

  stopTrackVoices(trackIndex) {
    const stops = this.trackActiveStops.get(trackIndex)
    if (!stops) return
    for (const stop of stops) {
      try { stop() } catch {}
    }
    this.trackActiveStops.delete(trackIndex)
  }

  setTrackVolume(trackIndex, volume) {
    const value = Math.max(0, Math.min(1, Number(volume) || 0))
    const plannedTrack = this.playbackPlan?.tracks.find(item => item.trackIndex === trackIndex)
    if (plannedTrack) plannedTrack.track.volume = value
    const gainNode = this.trackGains.get(trackIndex)
    if (gainNode && this.context) gainNode.gain.setValueAtTime(value, this.context.currentTime)
    if (this.playbackPlan && this.masterGain && this.context) {
      this.masterGain.gain.setValueAtTime(calculatePlaybackMasterGain(this.playbackPlan), this.context.currentTime)
    }
  }

  dispose() {
    this.stop()
    if (this.context) {
      try { this.context.close() } catch {}
    }
    this.context = null
    this.masterGain = null
    this.noiseBuffer = null
  }

  async auditionNote(track, midi, durationMs = 300) {
    await this.auditionNotes(track, [{ note: midi, durationMs }])
  }

  // Auditions one or more notes that sound together, each keeping its own duration.
  async auditionNotes(track, notes) {
    const requested = (Array.isArray(notes) ? notes : [])
      .filter(entry => entry?.note != null)
      .map(entry => ({ note: entry.note, duration: Math.max(0.05, Number(entry.durationMs) / 1000 || 0.3) }))
    if (!requested.length) return
    this.stopAudition()
    const context = this._ensureContext()
    await context.resume()
    this.masterGain.gain.cancelScheduledValues(context.currentTime)
    this.masterGain.gain.setValueAtTime(MASTER_ATTENUATION, context.currentTime)
    this.auditioning = true
    const startTime = context.currentTime + 0.01
    const synthTrack = normalizeSynthResource({ tracks: [track] }).tracks[0]
    for (const { note, duration } of requested) {
      for (const layer of synthTrack.layers) {
        if (!layer?.enabled) continue
        this._scheduleVoice({
          context,
          startTime,
          duration,
          note,
          track: synthTrack,
          layer: { ...layer, volumePercent: layer.volumePercent * synthTrack.volume },
          audition: true,
        })
      }
    }
    const longestDuration = Math.max(...requested.map(entry => entry.duration))
    const timer = window.setTimeout(() => this.stopAudition(), Math.max(0, (startTime + longestDuration + (Number(synthTrack.adsr?.release) || 0.2) + 0.05 - context.currentTime) * 1000))
    this._stopTimers.add(timer)
  }

  stopAudition() {
    this.auditioning = false
    for (const stop of this.auditionStops) {
      try { stop() } catch {}
    }
    this.auditionStops = []
  }

  _ensureContext() {
    if (this.context) return this.context
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    this.context = new AudioCtx()
    this.masterGain = this.context.createGain()
    this.masterGain.gain.value = 0
    this.masterGain.connect(this.context.destination)
    this.noiseBuffer = createNoiseBuffer(this.context)
    return this.context
  }

  _scheduleVoice({ context, startTime, duration, note, track, layer, outputNode = this.masterGain, audition = false, trackIndex = null }) {
    if (!layer?.enabled || !track) return
    const osc = createVoiceOscillator(context, layer.waveform, layer.pulseWidthPercent, this.noiseBuffer)
    const amp = context.createGain()
    const vibrato = track.vibrato.enabled ? createVibrato(context, track.vibrato.rateHz, track.vibrato.depthHz) : null
    const layerGain = context.createGain()
    const outputGain = context.createGain()

    const frequency = midiToFrequency(note)
    const detuneHz = centsToRatio(layer.detuneCents)
    const amplitude = Math.max(0, Math.min(1, (layer.volumePercent ?? 100) / 100))

    outputGain.gain.value = amplitude
    layerGain.gain.value = 1
    amp.gain.value = 0

    osc.connect(amp)
    amp.connect(outputGain)
    outputGain.connect(layerGain)

    if (vibrato) {
      vibrato.start(startTime)
      vibrato.connect(osc.frequency)
    }

    layerGain.connect(outputNode)

    osc.frequency.setValueAtTime(frequency * detuneHz, startTime)
    applyAdsr(context, amp.gain, startTime, duration, track.adsr)

    osc.start(startTime)
    osc.stop(startTime + duration + track.adsr.release + 0.05)

    let cleaned = false
    let stopping = false
    const cleanup = () => {
      if (cleaned) return
      cleaned = true
      try { osc.disconnect() } catch {}
      try { amp.disconnect() } catch {}
      try { outputGain.disconnect() } catch {}
      try { layerGain.disconnect() } catch {}
      if (vibrato) {
        try { vibrato.disconnect() } catch {}
      }
      this.activeStops.delete(stopVoice)
    }
    const stopVoice = () => {
      if (cleaned || stopping) return
      stopping = true
      const now = context.currentTime
      const stopTime = now + ANTI_CLICK_RELEASE_SECONDS
      rampGainToSilence(amp.gain, now, stopTime)
      try { osc.stop(stopTime) } catch {}
      window.setTimeout(cleanup, ANTI_CLICK_RELEASE_SECONDS * 1000)
    }
    this.activeStops.add(stopVoice)
    if (Number.isInteger(trackIndex)) {
      if (!this.trackActiveStops.has(trackIndex)) this.trackActiveStops.set(trackIndex, new Set())
      this.trackActiveStops.get(trackIndex).add(stopVoice)
    }
    if (audition) this.auditionStops.push(stopVoice)

    const releaseEnd = startTime + duration + track.adsr.release + 0.05
    const timer = window.setTimeout(() => {
      this._stopTimers.delete(timer)
      cleanup()
    }, Math.max(0, (releaseEnd - context.currentTime) * 1000))
    this._stopTimers.add(timer)
  }

  _scheduleAhead(generation = this.playbackGeneration) {
    if (generation !== this.playbackGeneration) return
    if (!this.playing || !this.context || this.playbackStartTime == null) return
    const horizon = this.context.currentTime + SCHEDULE_AHEAD_SECONDS
    const earliestStartTime = Math.max(this.context.currentTime, this.audioStartContextTime ?? this.context.currentTime)
    for (const plannedTrack of this.scheduledTracks) {
      const playbackSeconds = Math.max(0, earliestStartTime - this.playbackStartTime)
      const nextEvent = plannedTrack.events[plannedTrack.nextEventIndex]
      if (nextEvent && nextEvent.endSeconds <= playbackSeconds) {
        plannedTrack.nextEventIndex = findFirstEventEndingAfter(plannedTrack.events, playbackSeconds)
      }
      while (plannedTrack.nextEventIndex < plannedTrack.events.length) {
        const event = plannedTrack.events[plannedTrack.nextEventIndex]
        const eventStartTime = this.playbackStartTime + event.startSeconds
        if (eventStartTime > horizon) break
        plannedTrack.nextEventIndex++
        if (event.note == null || event.endSeconds <= 0) continue
        const eventEndTime = this.playbackStartTime + event.endSeconds
        if (eventEndTime <= earliestStartTime) continue
        const startTime = Math.max(eventStartTime, earliestStartTime)
        const duration = Math.max(0.001, eventEndTime - startTime)
        if (plannedTrack.track.enabled === false) continue
        for (const layer of plannedTrack.layers) {
          if (!layer?.enabled) continue
          this._scheduleVoice({ context: this.context, startTime, duration, note: event.note, track: plannedTrack.track, layer, outputNode: this.trackGains.get(plannedTrack.trackIndex), trackIndex: plannedTrack.trackIndex })
        }
      }
    }
  }

  _startScheduling(startTime) {
    const position = this.playbackPositionSeconds
    this.audioStartContextTime = startTime
    this.playbackStartOffsetSeconds = position
    this.playbackStartTime = startTime - position
    this.scheduledTracks = this.playbackPlan.tracks.map(track => ({
      ...track,
      nextEventIndex: findFirstEventEndingAfter(track.events, position),
    }))
    const generation = ++this.playbackGeneration
    this._scheduleAhead(generation)
    this.schedulerTimer = window.setInterval(() => this._scheduleAhead(generation), SCHEDULER_INTERVAL_MS)
  }

  _schedulePlaybackEnd() {
    const release = this._maxReleaseSeconds(this.playbackPlan.synth)
    const stopAt = this.playbackStartTime + this.playbackDurationSeconds + release + 0.1
    const timer = window.setTimeout(() => this.stop(), Math.max(0, (stopAt - this._audibleContextTime()) * 1000))
    this._stopTimers.add(timer)
    return stopAt
  }

  // Scheduling runs on context.currentTime (what the graph is rendering); the playhead must
  // follow what the device is actually playing, which trails currentTime by the output latency.
  _audibleContextTime() {
    return audibleContextTime(this.context, typeof performance !== 'undefined' ? performance.now() : null)
  }

  // Starting a session must drop the anti-click ramp the previous stop() left scheduled,
  // otherwise the fresh session fades to silence a few milliseconds after it starts.
  _openMasterGain(level) {
    if (!this.masterGain || !this.context) return
    const now = this.context.currentTime
    this.masterGain.gain.cancelScheduledValues(now)
    this.masterGain.gain.setValueAtTime(level, now)
  }

  _cancelScheduledPlayback() {
    this.playbackGeneration++
    if (this.schedulerTimer != null) window.clearInterval(this.schedulerTimer)
    this.schedulerTimer = null
    for (const timer of this._stopTimers) window.clearTimeout(timer)
    this._stopTimers.clear()
    for (const stop of this.activeStops) {
      try { stop() } catch {}
    }
    this.activeStops.clear()
    this.trackActiveStops.clear()
  }

  _createTrackGains(plan) {
    this._disconnectTrackGains()
    if (typeof this.context?.createGain !== 'function') return
    for (const plannedTrack of plan.tracks) {
      const gainNode = this.context.createGain()
      gainNode.gain.value = plannedTrack.track.volume
      gainNode.connect(this.masterGain)
      this.trackGains.set(plannedTrack.trackIndex, gainNode)
    }
  }

  _disconnectTrackGains() {
    for (const gainNode of this.trackGains.values()) {
      window.setTimeout(() => {
        try { gainNode.disconnect() } catch {}
      }, ANTI_CLICK_RELEASE_SECONDS * 1000)
    }
    this.trackGains.clear()
  }

  _clampPosition(positionSeconds) {
    return Math.max(0, Math.min(this.playbackDurationSeconds, Number(positionSeconds) || 0))
  }

  _maxReleaseSeconds(synth) {
    return Math.max(...synth.tracks.map(track => Number(track.adsr?.release) || 0), 0)
  }
}

export function audibleContextTime(context, nowMs = null) {
  const currentTime = Number(context?.currentTime) || 0
  const timestamp = typeof context?.getOutputTimestamp === 'function' ? context.getOutputTimestamp() : null
  const contextTime = Number(timestamp?.contextTime)
  if (Number.isFinite(contextTime) && contextTime > 0) {
    const performanceTime = Number(timestamp.performanceTime)
    const sinceTimestamp = Number.isFinite(performanceTime) && nowMs != null && Number.isFinite(Number(nowMs))
      ? Math.min(MAX_TIMESTAMP_INTERPOLATION_SECONDS, Math.max(0, (Number(nowMs) - performanceTime) / 1000))
      : 0
    return Math.min(currentTime, contextTime + sinceTimestamp)
  }
  return currentTime - (Number(context?.outputLatency) || Number(context?.baseLatency) || 0)
}

export function createPlaybackPlan(resource, scope = { type: 'song' }) {
  const synth = normalizeSynthResource(resource)
  const included = getSynthPlaybackTrackEntries(synth, scope)
  const tracks = included.map(({ track, trackIndex }) => {
    const layers = Array.isArray(track.layers) ? track.layers.slice() : []
    const enabled = scope?.type === 'composer' ? (scope.enabledTrackIndices ?? []).includes(trackIndex) : track.enabled !== false
    let maxEndSeconds = 0
    const events = track.sequence.map(event => {
      const durationMs = eventDurationMs(event, synth.tempo.bpm)
      const startSeconds = ticksToSeconds(event.startTick, synth.tempo.bpm)
      const planned = { ...event, durationMs, startSeconds, endSeconds: startSeconds + durationMs / 1000 }
      maxEndSeconds = Math.max(maxEndSeconds, planned.endSeconds)
      Object.defineProperty(planned, 'maxEndSeconds', { value: maxEndSeconds, enumerable: false })
      return planned
    })
    const totalDurationSeconds = maxEndSeconds
    return { trackIndex, track: { ...track, enabled }, layers, events, totalDurationSeconds }
  })
  return {
    synth,
    tracks,
    totalDurationSeconds: Math.max(...tracks.map(track => track.totalDurationSeconds), 0),
  }
}

export function findFirstEventEndingAfter(events, playbackSeconds) {
  let low = 0
  let high = events?.length ?? 0
  while (low < high) {
    const middle = (low + high) >> 1
    if ((events[middle].maxEndSeconds ?? events[middle].endSeconds) <= playbackSeconds) low = middle + 1
    else high = middle
  }
  return low
}

export function calculatePlaybackMixPeak(plan) {
  const changes = []
  for (const plannedTrack of plan?.tracks ?? []) {
    const layerGain = (plannedTrack.layers ?? []).reduce((sum, layer) => layer?.enabled
      ? sum + Math.max(0, Math.min(1, (Number(layer.volumePercent) || 0) / 100))
      : sum, 0)
    const voiceCount = (plannedTrack.layers ?? []).filter(layer => layer?.enabled).length
    const weightedGain = layerGain * Math.max(0, Math.min(1, Number(plannedTrack.track?.volume) || 0))
    const release = Math.max(0, Number(plannedTrack.track?.adsr?.release) || 0)
    for (const event of plannedTrack.events ?? []) {
      if (event.note == null || event.endSeconds <= event.startSeconds) continue
      changes.push({ time: event.startSeconds, gain: weightedGain, voices: voiceCount })
      changes.push({ time: event.endSeconds + release, gain: -weightedGain, voices: -voiceCount })
    }
  }
  changes.sort((a, b) => a.time - b.time || a.gain - b.gain)

  let gain = 0
  let voices = 0
  let peakGain = 0
  let peakVoices = 0
  for (const change of changes) {
    gain += change.gain
    voices += change.voices
    peakGain = Math.max(peakGain, gain)
    peakVoices = Math.max(peakVoices, voices)
  }
  return { peakGain, peakVoices }
}

export function calculatePlaybackMasterGain(plan) {
  const { peakGain } = calculatePlaybackMixPeak(plan)
  return peakGain > 0 ? Math.min(MASTER_ATTENUATION, MAX_MASTER_PEAK / peakGain) : MASTER_ATTENUATION
}

function ticksToSeconds(ticks, bpm) {
  return Math.max(0, Number(ticks) || 0) / 480 * (60 / Math.max(1, Number(bpm) || 120))
}

export function midiToFrequency(midi) {
  return 440 * Math.pow(2, (Number(midi) - 69) / 12)
}

export function centsToRatio(cents) {
  return Math.pow(2, Number(cents) / 1200)
}

export function pulseFourierCoefficients(dutyCyclePercent, harmonics = PULSE_HARMONICS) {
  const duty = Math.min(100, Math.max(0, Number(dutyCyclePercent))) / 100
  const real = new Float32Array(harmonics + 1)
  const imag = new Float32Array(harmonics + 1)
  for (let n = 1; n <= harmonics; n++) {
    const coeff = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * duty)
    imag[n] = coeff
  }
  return { real, imag }
}

function createVoiceOscillator(context, waveform, pulseWidthPercent, noiseBuffer) {
  if (waveform === 'noise') {
    const source = context.createBufferSource()
    source.buffer = noiseBuffer
    source.loop = true
    return source
  }
  const osc = context.createOscillator()
  if (waveform === 'triangle' || waveform === 'saw') {
    osc.type = waveform === 'saw' ? 'sawtooth' : 'triangle'
    return osc
  }
  if (waveform === 'square') {
    osc.setPeriodicWave(buildPulseWave(context, pulseWidthPercent))
    return osc
  }
  osc.type = SYNTH_WAVEFORMS.includes(waveform) ? waveform : 'sine'
  return osc
}

function buildPulseWave(context, pulseWidthPercent) {
  const { real, imag } = pulseFourierCoefficients(pulseWidthPercent)
  return context.createPeriodicWave(real, imag, { disableNormalization: false })
}

function createVibrato(context, rateHz, depthHz) {
  const lfo = context.createOscillator()
  const depthGain = context.createGain()
  lfo.frequency.value = Math.max(0, Number(rateHz) || 0)
  depthGain.gain.value = Math.max(0, Number(depthHz) || 0)
  lfo.connect(depthGain)
  depthGain.connect(lfo.frequency)
  return lfo
}

function createNoiseBuffer(context) {
  const length = DEFAULT_SAMPLE_RATE
  const buffer = context.createBuffer(1, length, DEFAULT_SAMPLE_RATE)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1
  return buffer
}

function applyAdsr(context, gainParam, startTime, duration, adsr) {
  const attack = Math.max(0, Number(adsr?.attack) || 0)
  const decay = Math.max(0, Number(adsr?.decay) || 0)
  const sustain = Math.min(1, Math.max(0, Number(adsr?.sustain) || 0))
  const release = Math.max(0, Number(adsr?.release) || 0)
  const peak = 1
  const sustainLevel = peak * sustain
  const noteOff = startTime + duration

  gainParam.cancelScheduledValues(startTime)
  gainParam.setValueAtTime(0, startTime)
  gainParam.linearRampToValueAtTime(peak, startTime + Math.max(0.001, attack))
  gainParam.linearRampToValueAtTime(sustainLevel, startTime + Math.max(0.001, attack + decay))
  gainParam.setValueAtTime(sustainLevel, noteOff)
  gainParam.linearRampToValueAtTime(0, noteOff + Math.max(0.001, release))
}

function rampGainToSilence(gainParam, now, stopTime = now + ANTI_CLICK_RELEASE_SECONDS) {
  try {
    if (typeof gainParam.cancelAndHoldAtTime === 'function') {
      gainParam.cancelAndHoldAtTime(now)
    } else {
      const currentValue = Number(gainParam.value) || 0
      gainParam.cancelScheduledValues(now)
      gainParam.setValueAtTime(currentValue, now)
    }
    gainParam.linearRampToValueAtTime(0, stopTime)
  } catch {}
}
